import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { EiiApi } from "../../api/EiiApi";
import type { CreateEiiOverride } from "../../generated/dto/CreateEiiOverride";
import type { DeleteEiiOverrideStaff } from "../../generated/dto/DeleteEiiOverrideStaff";
import type { EiiApplicationRequest } from "../../generated/dto/EiiApplicationRequest";
import type { EiiApplicationStatus } from "../../generated/dto/EiiApplicationStatus";
import type { EiiCreateResponseRequest } from "../../generated/dto/EiiCreateResponseRequest";
import type { EiiMitStaff } from "../../generated/dto/EiiMitStaff";
import type { EiiOverrideSearchParams } from "../../generated/dto/EiiOverrideSearchParams";
import type { EiiOverrideStaff } from "../../generated/dto/EiiOverrideStaff";
import type { EiiOverrideStaffSearchResult } from "../../generated/dto/EiiOverrideStaffSearchResult";
import type { EiiResponseStaff } from "../../generated/dto/EiiResponseStaff";
import type { EiiResponseStaffSearchParams } from "../../generated/dto/EiiResponseStaffSearchParams";
import type { EiiResponseStaffSearchResult } from "../../generated/dto/EiiResponseStaffSearchResult";
import type { EiiUpdateResponseRequest } from "../../generated/dto/EiiUpdateResponseRequest";
import type { UpdateEiiOverride } from "../../generated/dto/UpdateEiiOverride";
import type { PaginationResult } from "../../generated/dto/PaginationResult";

export class EiiApiClient implements EiiApi {
  constructor(private readonly client = useClient()) {}

  async getEiiResponseStaff(id: number): Promise<EiiResponseStaff> {
    const { data } = await this.client.get<unknown>(`/eiiresponse/staff/${id}`);
    return deserializeCbiDto<EiiResponseStaff>(data, "generated.dto.EiiResponseStaff");
  }

  async getEiiMitStaff(id: number): Promise<EiiMitStaff> {
    const { data } = await this.client.get<unknown>(`/eiimit/staff/${id}`);
    return deserializeCbiDto<EiiMitStaff>(data, "generated.dto.EiiMitStaff");
  }

  async getEiiOverrideStaff(id: number): Promise<EiiOverrideStaff> {
    const { data } = await this.client.get<unknown>(`/eiioverride/staff/${id}`);
    return deserializeCbiDto<EiiOverrideStaff>(data, "generated.dto.EiiOverrideStaff");
  }

  async getAllEiiMitStaffs(): Promise<Array<EiiMitStaff>> {
    const { data } = await this.client.get<unknown>(`/eiimit/staff/all`);
    return deserializeCbiDto<Array<EiiMitStaff>>(data, "generated.dto.EiiMitStaff");
  }

  async createEiiOverrideStaff(source: CreateEiiOverride): Promise<EiiOverrideStaff> {
    const { data } = await this.client.post<unknown>(`/eiioverride/staff`, serializeCbiDto(source, "generated.dto.CreateEiiOverride"));
    return deserializeCbiDto<EiiOverrideStaff>(data, "generated.dto.EiiOverrideStaff");
  }

  async updateEiiOverrideStaff(source: UpdateEiiOverride): Promise<EiiOverrideStaff> {
    const { data } = await this.client.put<unknown>(`/eiioverride/staff/${source.overrideId}`, serializeCbiDto(source, "generated.dto.UpdateEiiOverride"));
    return deserializeCbiDto<EiiOverrideStaff>(data, "generated.dto.EiiOverrideStaff");
  }

  async deleteEiiOverrideStaff(source: DeleteEiiOverrideStaff): Promise<void> {
    await this.client.delete(`/eiioverride/staff/${source.overrideId}`);
  }

  async searchEiiResponseStaff(params: EiiResponseStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<EiiResponseStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/eiiresponse/staff/search`, serializeCbiDto(params, "generated.dto.EiiResponseStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<EiiResponseStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<EiiResponseStaffSearchResult>>(result.items, "generated.dto.EiiResponseStaffSearchResult") };
    return result;
  }

  async searchEiiOverrideStaff(params: EiiOverrideSearchParams, page: number, perPage: number): Promise<PaginationResult<EiiOverrideStaffSearchResult>> {
    const { data } = await this.client.post<unknown>(`/eiioverride/staff/search`, serializeCbiDto(params, "generated.dto.EiiOverrideSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<EiiOverrideStaffSearchResult>;
    result = { ...result, items: deserializeCbiDto<Array<EiiOverrideStaffSearchResult>>(result.items, "generated.dto.EiiOverrideStaffSearchResult") };
    return result;
  }

  async searchEiiResponseStaffFull(params: EiiResponseStaffSearchParams, page: number, perPage: number): Promise<PaginationResult<EiiResponseStaff>> {
    const { data } = await this.client.post<unknown>(`/eiiresponse/staff/search/full`, serializeCbiDto(params, "generated.dto.EiiResponseStaffSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<EiiResponseStaff>;
    result = { ...result, items: deserializeCbiDto<Array<EiiResponseStaff>>(result.items, "generated.dto.EiiResponseStaff") };
    return result;
  }

  async searchEiiOverrideStaffFull(params: EiiOverrideSearchParams, page: number, perPage: number): Promise<PaginationResult<EiiOverrideStaff>> {
    const { data } = await this.client.post<unknown>(`/eiioverride/staff/search/full`, serializeCbiDto(params, "generated.dto.EiiOverrideSearchParams"), { params: { page, perPage } });
    let result = data as PaginationResult<EiiOverrideStaff>;
    result = { ...result, items: deserializeCbiDto<Array<EiiOverrideStaff>>(result.items, "generated.dto.EiiOverrideStaff") };
    return result;
  }

  async createResponseStaff(request: EiiCreateResponseRequest): Promise<EiiResponseStaff> {
    const { data } = await this.client.post<unknown>(`/eii/staff/createresponse`, serializeCbiDto(request, "generated.dto.EiiCreateResponseRequest"));
    return deserializeCbiDto<EiiResponseStaff>(data, "generated.dto.EiiResponseStaff");
  }

  async currentApplicationMember(): Promise<EiiApplicationStatus> {
    const { data } = await this.client.post<unknown>(`/eii/member/currentapplication`);
    return deserializeCbiDto<EiiApplicationStatus>(data, "generated.dto.EiiApplicationStatus");
  }

  async submitApplicationMember(request: EiiApplicationRequest): Promise<EiiApplicationStatus> {
    const { data } = await this.client.post<unknown>(`/eii/member/submitapplication`, serializeCbiDto(request, "generated.dto.EiiApplicationRequest"));
    return deserializeCbiDto<EiiApplicationStatus>(data, "generated.dto.EiiApplicationStatus");
  }

  async updateResponseStaff(request: EiiUpdateResponseRequest): Promise<EiiResponseStaff> {
    const { data } = await this.client.post<unknown>(`/eii/staff/updateresponse`, serializeCbiDto(request, "generated.dto.EiiUpdateResponseRequest"));
    return deserializeCbiDto<EiiResponseStaff>(data, "generated.dto.EiiResponseStaff");
  }
}

export const useEiiApi = (): EiiApi => new EiiApiClient();
